// Navigation helpers for the event workspace (plan 2139). Pure apart from the sessionStorage list-URL memory, so they are unit-tested.

const LIST_URL_KEY = 'tec.events.listUrl';
const LIST_URL_RE = /^\/events(\?[^#]*)?$/;

export const isListUrl = (v) => typeof v === 'string' && LIST_URL_RE.test(v);

// The list view the user left (filters, search, sort, calendar state), so "Back to Events" returns to exactly that.
export function rememberEventsList(search) {
  const url = `/events${search || ''}`;
  if (!isListUrl(url)) return;
  try { sessionStorage.setItem(LIST_URL_KEY, url); } catch (_) { /* private window: fall back to the plain list */ }
}

export function getEventsListUrl() {
  try {
    const v = sessionStorage.getItem(LIST_URL_KEY);
    return isListUrl(v) ? v : '/events';
  } catch (_) {
    return '/events';
  }
}

// ── Tabs, sections and links (plan 2142) ─────────────────────────────────────────────────────────────────────────────
// The ONLY place that defines the event page set, its labels and its URLs. Components must build every in-event link with
// eventLink() and take wording from LABELS/EVENT_TABS (eventNav.test.js scans the source for strays).
// `needs` is the events action the tab requires (the setup route needs events:create).
export const EVENT_TABS = [
  { key: 'overview', label: 'Overview', suffix: '' },
  { key: 'setup', label: 'Setup', suffix: '/setup', needs: 'create' },
  { key: 'tags', label: 'Tags', suffix: '/tags' },
  { key: 'registrants', label: 'Registrants', suffix: '/registrations' },
  { key: 'communications', label: 'Communications', suffix: '/communications' },
  { key: 'credentials', label: 'Credentials', suffix: '/credentials' },
  { key: 'attendance', label: 'Attendance', suffix: '/attendance' },
  { key: 'results', label: 'Results', suffix: '/results' },
];

// Anchored sections inside a tab (one level only, no nested tabs): [anchor, label].
export const SECTIONS = {
  setup: [['basics', 'Basics'], ['schedule', 'Schedule'], ['location', 'Location and links'], ['registration', 'Registration'],
    ['agenda', 'Agenda'], ['dates', 'Multiple dates'], ['targeting', 'Tags and targeting'], ['audience', 'Audience'],
    ['team', 'Presenters and co-admins'], ['publishing', 'Publishing']],
  communications: [['email-log', 'Email log'], ['message', 'Message registrants'], ['invitations', 'Invitation waves']],
  results: [['health', 'Health'], ['analytics', 'Impact analytics'], ['meeting-summary', 'Meeting summary']],
};

// Old URLs (email links, bookmarks, specs) keep working: they redirect to the tab and section that now hold the content.
export const LEGACY_EVENT_PATHS = {
  edit: { tab: 'setup' },
  'email-log': { tab: 'communications', anchor: 'email-log' },
  health: { tab: 'results', anchor: 'health' },
  analytics: { tab: 'results', anchor: 'analytics' },
};

// Wording used outside the tab strip, so a button, card and tooltip never disagree with the tab it opens.
export const LABELS = {
  backToList: 'Back to Events',
  switchEvent: 'Switch event',
  registrants: 'Registrants',
  attendance: 'Attendance',
  emailLog: 'Email log',
  credentials: 'Credentials',
};

const tabByKey = (key) => {
  const t = EVENT_TABS.find((x) => x.key === key);
  if (!t) throw new Error(`Unknown event tab "${key}"`);
  return t;
};

// Every in-event link: eventLink(12, 'registrants', { params: { status: 'pending_review' } }) -> /events/12/registrations?status=pending_review
export function eventLink(id, tab, { anchor, params, sub } = {}) {
  const qs = params ? new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')).toString() : '';
  return `/events/${id}${tabByKey(tab).suffix}${sub ? `/${sub}` : ''}${qs ? `?${qs}` : ''}${anchor ? `#${anchor}` : ''}`;
}

export function visibleTabs(can) {
  return EVENT_TABS.filter((t) => !t.needs || can('events', t.needs));
}

// Active tab for a pathname like /events/12/registrations; legacy suffixes map to their new tab; anything else is the overview.
export function tabKeyForPath(pathname) {
  const m = /^\/events\/\d+(\/[^/?#]*)?/.exec(pathname || '');
  const suffix = m && m[1] ? m[1] : '';
  const direct = EVENT_TABS.find((t) => t.suffix === suffix);
  if (direct) return direct.key;
  const legacy = LEGACY_EVENT_PATHS[suffix.replace(/^\//, '')];
  return legacy ? legacy.tab : 'overview';
}

// Switching to another event keeps you on the same tab.
export function switchTargetPath(pathname, newId) {
  return eventLink(newId, tabKeyForPath(pathname));
}

export const isEventPath = (pathname) => /^\/events\/\d+(\/|$)/.test(pathname || '');

const STATUS_COLORS = {
  published: 'success', draft: 'default', ended: 'info', cancelled: 'error', pending_review: 'warning', registration_closed: 'warning',
};
export const statusColor = (status) => STATUS_COLORS[status] || 'default';
export const statusLabel = (status) => (status ? status.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) : '—');

// Attention levels sort warnings first.
export const sortAttention = (items) => [...items].sort((a, b) => (a.level === b.level ? 0 : a.level === 'warn' ? -1 : 1));

// Where each server attention item is fixed: the exact section or filter, not just the tab.
const ATTENTION_TARGETS = {
  not_published: ['setup', { anchor: 'publishing' }],
  attendance_missing: ['attendance', {}],
  pending_review: ['registrants', { params: { status: 'pending_review' } }],
  emails_undelivered: ['communications', { anchor: 'email-log', params: { status: 'failed' } }],
  waitlist_with_seats: ['registrants', { params: { status: 'waitlisted' } }],
  no_capacity: ['setup', { anchor: 'registration' }],
  no_tags: ['setup', { anchor: 'targeting' }],
};
const SERVER_TAB_TO_TAB = { details: 'setup', registrants: 'registrants', attendance: 'attendance', emails: 'communications', health: 'results' };

export function attentionLink(id, item) {
  const target = ATTENTION_TARGETS[item.key];
  if (target) return eventLink(id, target[0], target[1]);
  return eventLink(id, SERVER_TAB_TO_TAB[item.tab] || 'overview');
}

// Lifecycle strip (plan 2142). A cancelled event is not on the line, so it returns null.
export const LIFECYCLE = ['Draft', 'Published', 'Registration closed', 'Held', 'Reported'];
export function lifecycleStage(status, hasAttendance) {
  switch (status) {
    case 'draft': case 'pending_review': return 0;
    case 'published': return 1;
    case 'registration_closed': return 2;
    case 'ended': return hasAttendance ? 4 : 3;
    default: return null;
  }
}

// The one primary action for the header: what the admin most likely needs to do next. `to` navigates, `action` opens a dialog.
export function primaryAction(summary, can) {
  if (!summary) return null;
  const { event, attention, can_edit: canEdit } = summary;
  const warn = (key) => attention.some((a) => a.key === key);
  if (event.status === 'pending_review' && can('events', 'admin')) return { key: 'approve', label: 'Approve event', action: 'approve' };
  if (event.status === 'draft' && canEdit && can('events', 'create')) {
    return { key: 'publish', label: 'Publish event', to: eventLink(event.id, 'setup', { anchor: 'publishing' }) };
  }
  if (warn('attendance_missing')) return { key: 'attendance', label: 'Upload attendance', to: eventLink(event.id, 'attendance') };
  if (warn('pending_review')) return { key: 'review', label: 'Review approvals', to: attentionLink(event.id, { key: 'pending_review' }) };
  if (['published', 'registration_closed'].includes(event.status) && can('admin.access', 'write')) {
    return { key: 'message', label: 'Message registrants', action: 'message' };
  }
  if (canEdit && can('events', 'create')) return { key: 'edit', label: 'Edit event', to: eventLink(event.id, 'setup') };
  return null;
}

// Which lifecycle controls an event in this status offers (the API returns 409 for the rest). One source for the header menu and Overview.
export function lifecycleActions(status) {
  return {
    unpublish: ['published', 'registration_closed'].includes(status),
    cancel: ['draft', 'pending_review', 'published', 'registration_closed'].includes(status),
    reinstate: status === 'cancelled',
  };
}

// Tab badges: a count where the number is the work, a dot where only the presence matters (Carbon guidance).
export function tabBadges(summary) {
  if (!summary) return {};
  const out = {};
  if (summary.registrations.pending_review > 0) out.registrants = { count: summary.registrations.pending_review };
  if (summary.emails && summary.emails.undelivered > 0) out.communications = { dot: true };
  if (summary.attention.some((a) => a.key === 'attendance_missing')) out.attendance = { dot: true };
  return out;
}

// Readiness checklist for Overview and Results, derived from the summary. `ok` is null when the check does not apply
// (for example attendance before the event has ended, or email checks for people who cannot see the email log). `to` is the
// exact place the problem is fixed.
export function readinessChecks(summary) {
  if (!summary) return [];
  const { event, registrations: regs, emails, attendance } = summary;
  const id = event.id;
  const cancelled = event.status === 'cancelled';
  const live = ['published', 'ended', 'registration_closed'].includes(event.status);
  // Setup checks only matter while the event can still be run; an ended or cancelled event is not asked to add capacity.
  const setup = ['draft', 'published', 'registration_closed'].includes(event.status);
  return [
    { key: 'published', label: 'Published, so people can register', ok: cancelled ? null : live, to: eventLink(id, 'setup', { anchor: 'publishing' }), verb: 'Publish' },
    { key: 'capacity', label: 'Capacity is set', ok: setup ? !!event.capacity : null, to: eventLink(id, 'setup', { anchor: 'registration' }), verb: 'Set capacity' },
    { key: 'tags', label: 'Product tags are set', ok: setup ? summary.tags_count > 0 : null, to: eventLink(id, 'setup', { anchor: 'targeting' }), verb: 'Add tags' },
    { key: 'presenters', label: 'Presenters are assigned', ok: setup ? summary.presenters_count > 0 : null, to: eventLink(id, 'setup', { anchor: 'team' }), verb: 'Add presenters' },
    { key: 'review', label: 'No registrations are waiting for review', ok: regs.pending_review === 0, to: attentionLink(id, { key: 'pending_review' }), verb: 'Review' },
    { key: 'emails', label: 'No emails went undelivered in the last 30 days', ok: emails ? emails.undelivered === 0 : null, to: attentionLink(id, { key: 'emails_undelivered' }), verb: 'View log' },
    { key: 'attendance', label: 'Attendance has been uploaded', ok: event.status === 'ended' ? attendance.has_attendance : null, to: eventLink(id, 'attendance'), verb: 'Upload' },
  ];
}

// Quick-focus filters for the events list (plan 2139). Pure, over the fields GET /events returns.
const DAY_MS = 86400000;
const startOf = (evt) => (evt.start_datetime ? new Date(String(evt.start_datetime).replace(' ', 'T')).getTime() : NaN);
export const isUpcoming = (evt, now) => !Number.isNaN(startOf(evt)) && startOf(evt) >= now - DAY_MS && evt.status !== 'ended' && evt.status !== 'cancelled';

export function eventNeedsAttention(evt, now) {
  return !!(
    (evt.status === 'draft' && isUpcoming(evt, now))
    || evt.attendance_missing
    || evt.pending_review_count > 0
    || evt.email_undelivered_count > 0
    || evt.pending_participant_changes
  );
}

export const FOCUS_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'attention', label: 'Needs attention' },
  { key: 'drafts', label: 'Drafts' },
  { key: 'past', label: 'Past' },
  { key: 'review', label: 'Pending review' },
  { key: 'attendance', label: 'Attendance missing' },
];

export function matchesFocus(evt, focus, now) {
  switch (focus) {
    case 'upcoming': return isUpcoming(evt, now);
    case 'attention': return eventNeedsAttention(evt, now);
    case 'drafts': return evt.status === 'draft';
    case 'past': return evt.status !== 'cancelled' && (evt.status === 'ended' || (!Number.isNaN(startOf(evt)) && startOf(evt) < now - DAY_MS && evt.status !== 'draft'));
    case 'review': return evt.pending_review_count > 0;
    case 'attendance': return !!evt.attendance_missing;
    default: return true;
  }
}

export function focusCounts(events, now) {
  return Object.fromEntries(FOCUS_OPTIONS.map((o) => [o.key, events.filter((e) => matchesFocus(e, o.key, now)).length]));
}
